import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import GaugeComponent from 'react-gauge-component';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import { 
  Package, AlertCircle,
  CheckCircle, XCircle, BarChart3, RefreshCw, Printer, X, Eye, Activity, User, Download, CreditCard
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
// Note: useReactToPrint removed - using manual print implementation for better compatibility

// ============================================
// ADMIN PICKING DASHBOARD - ENHANCED WITH ADVANCED ANALYTICS
// ============================================
// Monitor all picking operations in real-time with comprehensive data visualization

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// ============================================
// ID CARD COMPONENT (What gets printed)
// ============================================
const IDCard = React.forwardRef(({ picker, cardNumber }, ref) => {
  const barcodeRef = useRef(null);
  const [barcodeReady, setBarcodeReady] = useState(false);

  useEffect(() => {
    if (cardNumber && barcodeRef.current) {
      try {
        // Generate barcode using the 6-digit card number
        JsBarcode(barcodeRef.current, cardNumber, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 14,
          margin: 2,
          background: '#FFFFFF',
          lineColor: '#000000'
        });
        setBarcodeReady(true);
      } catch (err) {
        console.error('Error generating barcode:', err);
      }
    }
  }, [cardNumber]);

  if (!picker) return null;

  return (
    <div ref={ref} style={{ padding: '20px', pageBreakAfter: 'always' }}>
      <div style={{
        width: '85.6mm',
        height: '53.98mm',
        background: 'linear-gradient(135deg, #90bb44 0%, #7aa838 100%)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1, 
          background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.5) 10px, rgba(255,255,255,.5) 20px)' }} />
        
        <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {/* Header */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', marginBottom: '2px' }}>FOODLINE HYPER</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: '1px' }}>Warehouse Picker</div>
          </div>

          {/* Card Body */}
          <div style={{ background: 'white', borderRadius: '6px', padding: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Picker Avatar */}
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: 'linear-gradient(135deg, #90bb44 0%, #7aa838 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                {picker.name?.charAt(0)?.toUpperCase() || 'P'}
              </div>
            </div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>{picker.name || 'Picker Name'}</div>
              {/* Barcode - 6 digit card number */}
              <div style={{ background: 'white', padding: '2px 0' }}>
                {cardNumber ? (
                  <svg ref={barcodeRef} style={{ maxWidth: '100%', height: '55px' }}></svg>
                ) : (
                  <div style={{ fontSize: '10px', color: '#ef4444', padding: '10px 0' }}>
                    ⚠️ No card assigned
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.9)', textAlign: 'center' }}>Scan barcode to authenticate</div>
        </div>
      </div>
    </div>
  );
});

IDCard.displayName = 'IDCard';

// ============================================
// ID CARDS SECTION COMPONENT
// ============================================
const IDCardsSection = ({ pickers, selectedPickers, loadingPickers, setPickers, setSelectedPickers, setLoadingPickers, printRef }) => {
  const [idCards, setIdCards] = useState({}); // Map of user_id -> card_number
  const [localLoading, setLocalLoading] = useState(true);
  const [localPickers, setLocalPickers] = useState([]);
  
  useEffect(() => {
    fetchPickersLocal();
  }, []);

  const fetchPickersLocal = async () => {
    setLocalLoading(true);
    console.log('🔄 Fetching pickers...');
    
    try {
      // Fetch pickers
      const { data: pickersData, error: pickersError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'picker')
        .order('name');

      console.log('👥 Pickers result:', pickersData, pickersError);

      if (pickersError) {
        console.error('❌ Error:', pickersError);
        setLocalLoading(false);
        return;
      }

      if (pickersData && pickersData.length > 0) {
        console.log('✅ Found pickers:', pickersData.length);
        setLocalPickers(pickersData);
        setPickers(pickersData);
      } else {
        console.log('⚠️ No pickers found');
      }
      
      // Fetch card numbers
      const { data: cardsData, error: cardsError } = await supabase
        .from('id_cards')
        .select('*');
      
      console.log('📇 Cards result:', cardsData, cardsError);

      if (cardsData && cardsData.length > 0) {
        const cardMap = {};
        cardsData.forEach(card => {
          const pickerId = card.user_id || card.picker_id;
          if (pickerId && card.card_number) {
            cardMap[pickerId] = card.card_number;
          }
        });
        console.log('📇 Card map:', cardMap);
        setIdCards(cardMap);
      }
    } catch (err) {
      console.error('❌ Error:', err);
    }
    
    setLocalLoading(false);
  };

  // Use local state if parent state is empty
  const displayPickers = localPickers.length > 0 ? localPickers : pickers;
  const isLoading = localLoading;

  // Manual print function that works without react-to-print library issues
  const handlePrint = () => {
    if (!printRef.current || selectedPickers.length === 0) return;
    
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Picker ID Cards</title>
        <style>
          @page { size: A4; margin: 10mm; }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        </style>
      </head>
      <body>
        ${printContent}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const togglePicker = (pickerId) => {
    setSelectedPickers(prev => 
      prev.includes(pickerId) ? prev.filter(id => id !== pickerId) : [...prev, pickerId]
    );
  };

  const selectAll = () => setSelectedPickers(displayPickers.map(p => p.id));
  const deselectAll = () => setSelectedPickers([]);

  const selectedPickerData = displayPickers.filter(p => selectedPickers.includes(p.id));

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
        <div className="flex items-center gap-2">
          <CreditCard className="text-blue-600" size={20} />
          <p className="text-sm text-blue-800">
            <strong>Generate ID cards</strong> with barcodes for your pickers. Each card contains a unique barcode for scanner login.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Select Pickers</h2>
              <div className="space-x-2">
                <button onClick={selectAll} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Select All</button>
                <button onClick={deselectAll} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Deselect All</button>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading pickers...</p>
              </div>
            ) : displayPickers.length === 0 ? (
              <div className="text-center py-12">
                <User size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No pickers found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {displayPickers.map(picker => (
                  <label key={picker.id} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPickers.includes(picker.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  }`}>
                    <input type="checkbox" checked={selectedPickers.includes(picker.id)} onChange={() => togglePicker(picker.id)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" />
                    <div className="ml-3 flex-1">
                      <div className="font-semibold text-gray-900">{picker.name}</div>
                      <div className="text-sm text-gray-600">{picker.email}</div>
                      {idCards[picker.id] ? (
                        <div className="text-xs text-green-600 font-mono mt-1 font-bold">
                          Card: {idCards[picker.id]}
                        </div>
                      ) : (
                        <div className="text-xs text-red-500 mt-1">
                          ⚠️ No card assigned
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg p-6 sticky top-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="mb-6">
              <div className="text-sm text-gray-600 mb-2">Selected:</div>
              <div className="text-3xl font-bold text-blue-600">{selectedPickers.length}</div>
              <div className="text-sm text-gray-500">ID card(s)</div>
            </div>
            <button onClick={handlePrint} disabled={selectedPickers.length === 0}
              className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3">
              <Printer size={20} />
              Print ID Cards
            </button>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">📋 Instructions:</h3>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Select pickers from the list</li>
                <li>• Click "Print ID Cards" to print</li>
                <li>• Use PVC card printer or cardstock</li>
                <li>• Laminate cards for durability</li>
                <li>• Each barcode contains the card number</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedPickerData.map(picker => (
            <IDCard key={picker.id} picker={picker} cardNumber={idCards[picker.id]} />
          ))}
        </div>
      </div>
    </div>
  );
};

const AdminPickingDashboard = () => {
  const [stats, setStats] = useState({
    availableOrders: 0,
    inProgress: 0,
    completedToday: 0,
    withShortages: 0,
    avgPickTime: 0,
    pickingEfficiency: 0,
    shortageRate: 0,
    throughputRate: 0
  });
  const [activeSessions, setActiveSessions] = useState([]);
  const [recentCompletions, setRecentCompletions] = useState([]);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [hourlyTrends, setHourlyTrends] = useState([]);
  const [pickTimeDistribution, setPickTimeDistribution] = useState([]);
  const [shortageAnalysis, setShortageAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reprintModal, setReprintModal] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [orderDetailsModal, setOrderDetailsModal] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('today');
  const [pendingApprovals, setPendingApprovals] = useState([]);
  
  // ID Cards state
  const [pickers, setPickers] = useState([]);
  const [selectedPickers, setSelectedPickers] = useState([]);
  const [loadingPickers, setLoadingPickers] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    loadDashboardData();
    
    // Refresh every 3 minutes
    const interval = setInterval(loadDashboardData, 180000);
    return () => clearInterval(interval);
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live progress: poll only the active sessions every 3s so the progress bars
  // track item-by-item as pickers scan. Cheap query, and only while the tab is open.
  useEffect(() => {
    const tick = setInterval(() => {
      if (document.visibilityState === 'visible') loadActiveSessions();
    }, 3000);
    return () => clearInterval(tick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    setLoading(true);
    
    await Promise.all([
      loadStats(),
      loadActiveSessions(),
      loadRecentCompletions(),
      loadPerformanceStats(),
      loadHourlyTrends(),
      loadPickTimeDistribution(),
      loadShortageAnalysis(),
      loadPendingApprovals()
    ]);
    
    setLoading(false);
  };

  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    switch(dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case '7days':
        start.setDate(start.getDate() - 7);
        break;
      case '30days':
        start.setDate(start.getDate() - 30);
        break;
      default:
        start.setHours(0, 0, 0, 0);
    }
    
    return { start, end };
  };

  const loadStats = async () => {
    const { start, end } = getDateRange();
    
    // Available orders
    const { count: available } = await supabase
      .from('orders')
      .select('*', { count: 'exact', head: true })
      .in('status', ['confirmed', 'pending']);

    // In progress
    const { count: inProgress } = await supabase
      .from('picking_tasks')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'in_progress');

    // Completed in range
    const { data: completed, count: completedCount } = await supabase
      .from('picking_tasks')
      .select('started_at, completed_at, has_shortages')
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString());

    // With shortages in range
    const { count: shortages } = await supabase
      .from('picking_tasks')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'completed')
      .eq('has_shortages', true)
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString());

    // Calculate advanced metrics
    let avgPickTime = 0;
    let totalPickTime = 0;
    
    if (completed && completed.length > 0) {
      completed.forEach(task => {
        if (task.started_at && task.completed_at) {
          const pickTime = (new Date(task.completed_at) - new Date(task.started_at)) / 60000;
          totalPickTime += pickTime;
        }
      });
      avgPickTime = totalPickTime / completed.length;
    }

    const shortageRate = completedCount > 0 ? ((shortages / completedCount) * 100) : 0;
    const pickingEfficiency = avgPickTime > 0 ? Math.min(100, (15 / avgPickTime) * 100) : 0; // 15 min is ideal
    
    // Calculate throughput (orders per hour)
    const hoursInRange = (end - start) / (1000 * 60 * 60);
    const throughputRate = hoursInRange > 0 ? (completedCount / hoursInRange) : 0;

    setStats({
      availableOrders: available || 0,
      inProgress: inProgress || 0,
      completedToday: completedCount || 0,
      withShortages: shortages || 0,
      avgPickTime: avgPickTime,
      pickingEfficiency: pickingEfficiency,
      shortageRate: shortageRate,
      throughputRate: throughputRate
    });
  };

  const loadActiveSessions = async () => {
    const { data } = await supabase
      .from('picking_tasks')
      .select(`
        id,
        pick_number,
        started_at,
        picker_id,
        orders (
          order_number,
          order_items (
            id,
            quantity,
            picked_quantity
          )
        )
      `)
      .eq('status', 'in_progress')
      .order('started_at', { ascending: false });

    if (data) {
      const pickerIds = [...new Set(data.map(s => s.picker_id).filter(Boolean))];
      
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name')
        .in('id', pickerIds);
      
      const pickerNames = {};
      profiles?.forEach(p => {
        pickerNames[p.id] = p.name || `Picker ${p.id.substring(0, 8)}`;
      });


      const sessionsWithProgress = data.map((session) => {
        const totalItems = session.orders?.order_items?.length || 0;
        const pickedItems = session.orders?.order_items?.filter(
          item => item.picked_quantity > 0
        ).length || 0;

        const elapsedMinutes = Math.floor(
          (new Date() - new Date(session.started_at)) / 60000
        );

        return {
          ...session,
          pickerEmail: session.picker_id ? (pickerNames[session.picker_id] || 'Unknown') : 'Unknown',
          totalItems,
          pickedItems,
          progress: totalItems > 0 ? (pickedItems / totalItems) * 100 : 0,
          elapsedMinutes
        };
      });

      setActiveSessions(sessionsWithProgress);
    }
  };

  const loadRecentCompletions = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('picking_tasks')
      .select(`
        id,
        pick_number,
        completed_at,
        started_at,
        has_shortages,
        shortage_value,
        shortage_details,
        picker_id,
        qr_code_data,
        order_id,
        orders (
          id,
          order_number,
          user_id,
          delivery_address_snapshot
        )
      `)
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString())
      .order('completed_at', { ascending: false })
      .limit(10);

    if (data) {
      const pickerIds = [...new Set(data.map(s => s.picker_id).filter(Boolean))];
      
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name')
        .in('id', pickerIds);
      
      const pickerNames = {};
      profiles?.forEach(p => {
        pickerNames[p.id] = p.name || `Picker ${p.id.substring(0, 8)}`;
      });

      // Customers fetched separately — PostgREST has no orders→user_profiles relation
      const customerIds = [...new Set(data.map(d => d.orders?.user_id).filter(Boolean))];
      const customers = {};
      if (customerIds.length) {
        const { data: custs } = await supabase
          .from('user_profiles')
          .select('id, name, phone')
          .in('id', customerIds);
        custs?.forEach(c => { customers[c.id] = c; });
      }

      const completionsWithPicker = data.map((completion) => {
        const pickTime = Math.floor(
          (new Date(completion.completed_at) - new Date(completion.started_at)) / 60000
        );

        return {
          ...completion,
          pickerEmail: completion.picker_id ? (pickerNames[completion.picker_id] || 'Unknown') : 'Unknown',
          customer: customers[completion.orders?.user_id] || null,
          pickTime,
          pickTimeDisplay: pickTime === 0 ? '< 1 min' : `${pickTime} min`
        };
      });

      setRecentCompletions(completionsWithPicker);
    }
  };

  const loadPerformanceStats = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('picking_tasks')
      .select('picker_id, started_at, completed_at, has_shortages')
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString());

    if (data) {
      const pickerStats = {};
      
      for (const task of data) {
        if (!pickerStats[task.picker_id]) {
          pickerStats[task.picker_id] = {
            picker_id: task.picker_id,
            ordersCompleted: 0,
            totalPickTime: 0,
            shortages: 0,
            perfectPicks: 0
          };
        }

        const pickTime = (new Date(task.completed_at) - new Date(task.started_at)) / 60000;
        pickerStats[task.picker_id].ordersCompleted += 1;
        pickerStats[task.picker_id].totalPickTime += pickTime;
        
        if (task.has_shortages) {
          pickerStats[task.picker_id].shortages += 1;
        } else {
          pickerStats[task.picker_id].perfectPicks += 1;
        }
      }

      const pickerIds = Object.keys(pickerStats);
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name')
        .in('id', pickerIds);
      
      const pickerNames = {};
      profiles?.forEach(p => {
        pickerNames[p.id] = p.name || `Picker ${p.id.substring(0, 8)}`;
      });

      const statsArray = Object.values(pickerStats).map(stat => ({
        ...stat,
        pickerName: pickerNames[stat.picker_id] || 'Unknown',
        avgPickTime: stat.ordersCompleted > 0 ? (stat.totalPickTime / stat.ordersCompleted) : 0,
        accuracy: stat.ordersCompleted > 0 ? ((stat.perfectPicks / stat.ordersCompleted) * 100) : 0
      })).sort((a, b) => b.ordersCompleted - a.ordersCompleted);

      setPerformanceStats(statsArray);
    }
  };

  const loadHourlyTrends = async () => {
    const { start, end } = getDateRange();
    
    // Get picking tasks
    const { data: tasks } = await supabase
      .from('picking_tasks')
      .select('id, completed_at, started_at')
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString());

    // Get shortages count by task
    const { data: shortages } = await supabase
      .from('picking_shortages')
      .select('picking_task_id, created_at')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    // Count shortages per task
    const shortagesByTask = {};
    shortages?.forEach(s => {
      shortagesByTask[s.picking_task_id] = (shortagesByTask[s.picking_task_id] || 0) + 1;
    });

    if (tasks && tasks.length > 0) {
      const hourlyData = {};
      
      tasks.forEach(task => {
        const hour = new Date(task.completed_at).getHours();
        const hourKey = `${hour.toString().padStart(2, '0')}:00`;
        
        if (!hourlyData[hourKey]) {
          hourlyData[hourKey] = {
            hour: hourKey,
            orders: 0,
            totalPickTime: 0,
            shortages: 0
          };
        }
        
        hourlyData[hourKey].orders += 1;
        
        if (task.started_at && task.completed_at) {
          const pickTime = (new Date(task.completed_at) - new Date(task.started_at)) / 60000;
          hourlyData[hourKey].totalPickTime += pickTime;
        }
        
        if (shortagesByTask[task.id]) {
          hourlyData[hourKey].shortages += 1;
        }
      });

      const trendsArray = Object.values(hourlyData).map(h => ({
        ...h,
        avgPickTime: h.orders > 0 ? (h.totalPickTime / h.orders) : 0
      })).sort((a, b) => a.hour.localeCompare(b.hour));

      setHourlyTrends(trendsArray);
    } else {
      setHourlyTrends([]);
    }
  };

  const loadPickTimeDistribution = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('picking_tasks')
      .select('started_at, completed_at')
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString())
      .lte('completed_at', end.toISOString());

    if (data && data.length > 0) {
      const distribution = {
        '0-5 min': 0,
        '5-10 min': 0,
        '10-15 min': 0,
        '15-20 min': 0,
        '20-30 min': 0,
        '30+ min': 0
      };

      data.forEach(task => {
        if (task.started_at && task.completed_at) {
          const pickTime = (new Date(task.completed_at) - new Date(task.started_at)) / 60000;
          
          if (pickTime < 5) distribution['0-5 min'] += 1;
          else if (pickTime < 10) distribution['5-10 min'] += 1;
          else if (pickTime < 15) distribution['10-15 min'] += 1;
          else if (pickTime < 20) distribution['15-20 min'] += 1;
          else if (pickTime < 30) distribution['20-30 min'] += 1;
          else distribution['30+ min'] += 1;
        }
      });

      const distributionArray = Object.entries(distribution).map(([range, count]) => ({
        range,
        count
      }));

      setPickTimeDistribution(distributionArray);
    }
  };

  const loadShortageAnalysis = async () => {
    const { start, end } = getDateRange();
    
    // Query the picking_shortages table instead of JSON column
    const { data } = await supabase
      .from('picking_shortages')
      .select('reason, created_at')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    if (data && data.length > 0) {
      const reasonCounts = {};
      
      data.forEach(shortage => {
        const reason = shortage.reason || 'unknown';
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });

      const analysisArray = Object.entries(reasonCounts).map(([reason, count]) => ({
        reason: reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count
      })).sort((a, b) => b.count - a.count);

      setShortageAnalysis(analysisArray);
    } else {
      setShortageAnalysis([]);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      console.log('🔍 Loading pending approvals...');
      
      // Get shortages - simple query first
      const { data, error } = await supabase
        .from('picking_shortages')
        .select('*')
        .eq('wallet_credited', false)
        .order('created_at', { ascending: false });

      console.log('📊 Shortages raw data:', data);
      console.log('❌ Error:', error);

      if (error) throw error;
      if (!data || data.length === 0) {
        console.log('ℹ️ No shortages found');
        setPendingApprovals([]);
        return;
      }

      // Get order item IDs
      const orderItemIds = data.map(s => s.order_item_id);
      
      // Fetch order items with products
      const { data: orderItems } = await supabase
        .from('order_items')
        .select('id, order_id, price, product_id, products(name, image)')
        .in('id', orderItemIds);

      console.log('📦 Order items:', orderItems);

      // Get unique order IDs
      const orderIds = [...new Set(orderItems?.map(oi => oi.order_id) || [])];
      
      // Fetch orders
      const { data: orders } = await supabase
        .from('orders')
        .select('id, order_number, user_id')
        .in('id', orderIds);

      console.log('📋 Orders:', orders);

      // Get unique user IDs
      const userIds = [...new Set(orders?.map(o => o.user_id) || [])];

      // Fetch user profiles separately
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name, email')
        .in('id', userIds);

      console.log('👤 User profiles:', profiles);

      // Create lookups
      const orderItemMap = {};
      orderItems?.forEach(oi => {
        orderItemMap[oi.id] = oi;
      });

      const profileMap = {};
      profiles?.forEach(profile => {
        profileMap[profile.id] = profile;
      });

      const orderMap = {};
      orders?.forEach(order => {
        orderMap[order.id] = {
          ...order,
          user_profiles: profileMap[order.user_id]
        };
      });

      // Group shortages by order
      const groupedByOrder = {};
      
      data.forEach(shortage => {
        const orderItem = orderItemMap[shortage.order_item_id];
        if (!orderItem) {
          console.warn('No order item found for shortage:', shortage.id);
          return;
        }

        const orderId = orderItem.order_id;
        const order = orderMap[orderId];
        
        if (!order) {
          console.warn('No order found for order_id:', orderId);
          return;
        }
        
        if (!groupedByOrder[orderId]) {
          groupedByOrder[orderId] = {
            orderId,
            orderNumber: order.order_number,
            userName: order.user_profiles?.name || 'Unknown',
            userEmail: order.user_profiles?.email || '',
            userId: order.user_id,
            pickingTaskId: shortage.picking_task_id,
            shortages: [],
            totalRefund: 0,
            createdAt: shortage.created_at
          };
        }

        const shortQty = shortage.expected_quantity - shortage.picked_quantity;
        const refund = shortQty * (orderItem.price || 0);
        
        groupedByOrder[orderId].shortages.push({
          id: shortage.id,
          productName: orderItem.products?.name || 'Unknown Product',
          productImage: orderItem.products?.image,
          expectedQty: shortage.expected_quantity,
          pickedQty: shortage.picked_quantity,
          shortQty,
          price: orderItem.price,
          refund,
          reason: shortage.reason
        });
        
        groupedByOrder[orderId].totalRefund += refund;
      });

      const approvals = Object.values(groupedByOrder);
      console.log('✅ Final approvals:', approvals);
      setPendingApprovals(approvals);
      
    } catch (error) {
      console.error('💥 Error loading pending approvals:', error);
      setPendingApprovals([]);
    }
  };

  const handleApproveWalletCredit = async (approval) => {
    if (!window.confirm(`Approve R${approval.totalRefund.toFixed(2)} credit to ${approval.userName}?`)) {
      return;
    }

    try {
      const { data: currentBalance } = await supabase
        .rpc('get_wallet_balance', { p_user_id: approval.userId });
      
      const newBalance = (currentBalance || 0) + approval.totalRefund;
      
      const { error: walletError } = await supabase
        .from('wallet_transactions')
        .insert({
          user_id: approval.userId,
          order_id: approval.orderId,
          picking_task_id: approval.pickingTaskId,
          transaction_type: 'credit',
          amount: approval.totalRefund,
          balance_after: newBalance,
          reason: 'item_unavailable',
          description: `Admin approved refund for ${approval.shortages.length} unavailable item(s) from order #${approval.orderNumber}`
        });
      
      if (walletError) throw walletError;

      const shortageIds = approval.shortages.map(s => s.id);
      const { error: updateError } = await supabase
        .from('picking_shortages')
        .update({ wallet_credited: true })
        .in('id', shortageIds);
      
      if (updateError) throw updateError;

      alert(`✅ Successfully credited R${approval.totalRefund.toFixed(2)} to ${approval.userName}'s wallet`);
      loadPendingApprovals();
      
    } catch (error) {
      console.error('Error approving wallet credit:', error);
      alert('Failed to approve wallet credit: ' + error.message);
    }
  };

  const handleReprintLabel = async (completion) => {
    setReprintModal(completion);
    
    if (completion.qr_code_data) {
      try {
        const QRCode = (await import('qrcode')).default;
        const url = await QRCode.toDataURL(completion.qr_code_data, {
          width: 200,
          margin: 2
        });
        setQrCodeUrl(url);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewDetails = async (completion) => {
    setOrderDetailsModal(true);
    
    const { data: orderData } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          products (
            name,
            image
          )
        )
      `)
      .eq('id', completion.order_id)
      .single();

    if (orderData) {
      const shortageDetails = completion.shortage_details || [];
      
      const itemsWithShortageInfo = orderData.order_items?.map(item => ({
        ...item,
        shortage_info: shortageDetails.find(s => s.product_id === item.product_id)
      }));

      setOrderDetails({
        ...orderData,
        order_items: itemsWithShortageInfo,
        pickingTask: completion,
        shortageDetails
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BarChart3 className="text-blue-600" size={36} />
                Picking Operations Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Real-time monitoring and analytics</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
              </select>
              <button
                onClick={loadDashboardData}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics with Gauges */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            icon={<Package size={24} />}
            label="Available Orders"
            value={stats.availableOrders}
            color="blue"
          />
          <StatCard 
            icon={<Activity size={24} />}
            label="In Progress"
            value={stats.inProgress}
            color="yellow"
          />
          <StatCard 
            icon={<CheckCircle size={24} />}
            label="Completed"
            value={stats.completedToday}
            color="green"
          />
          <StatCard 
            icon={<AlertCircle size={24} />}
            label="With Shortages"
            value={stats.withShortages}
            color="orange"
          />
        </div>

        {/* Performance Gauges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Picking Efficiency</h3>
            <GaugeComponent
              type="radial"
              arc={{
                colorArray: ['#ef4444', '#f59e0b', '#10b981'],
                padding: 0.02,
                subArcs: [
                  { limit: 40 },
                  { limit: 70 },
                  { limit: 100 }
                ]
              }}
              pointer={{ type: "needle", animationDelay: 0 }}
              value={stats.pickingEfficiency}
              minValue={0}
              maxValue={100}
              labels={{
                valueLabel: {
                  style: { fontSize: '35px', fill: '#1f2937' },
                  formatTextValue: value => `${Math.round(value)}%`
                },
                tickLabels: {
                  type: 'inner',
                  ticks: [
                    { value: 0 },
                    { value: 50 },
                    { value: 100 }
                  ]
                }
              }}
            />
            <p className="text-center text-sm text-gray-600 mt-2">
              Based on avg pick time vs. 15 min target
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Accuracy Rate</h3>
            <GaugeComponent
              type="radial"
              arc={{
                colorArray: ['#ef4444', '#f59e0b', '#10b981'],
                padding: 0.02,
                subArcs: [
                  { limit: 80 },
                  { limit: 95 },
                  { limit: 100 }
                ]
              }}
              pointer={{ type: "needle", animationDelay: 0 }}
              value={100 - stats.shortageRate}
              minValue={0}
              maxValue={100}
              labels={{
                valueLabel: {
                  style: { fontSize: '35px', fill: '#1f2937' },
                  formatTextValue: value => `${Math.round(value)}%`
                },
                tickLabels: {
                  type: 'inner',
                  ticks: [
                    { value: 0 },
                    { value: 50 },
                    { value: 100 }
                  ]
                }
              }}
            />
            <p className="text-center text-sm text-gray-600 mt-2">
              Orders completed without shortages
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Throughput Rate</h3>
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-6xl font-bold text-blue-600">
                {stats.throughputRate.toFixed(1)}
              </div>
              <div className="text-xl text-gray-600 mt-2">orders/hour</div>
              <div className="mt-4 text-sm text-gray-500 text-center">
                Average pick time: <span className="font-semibold text-gray-700">
                  {stats.avgPickTime.toFixed(1)} min
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex gap-4 px-6">
              {['overview', 'performance', 'trends', 'shortages', 'wallet', 'idcards'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'idcards' ? 'ID Cards' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Active Sessions */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Active Picking Sessions</h3>
                  {activeSessions.length > 0 ? (
                    <div className="grid gap-4">
                      {activeSessions.map((session) => (
                        <div key={session.id} className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                                {session.pick_number}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Order #{session.orders?.order_number}</p>
                                {session.orders?.user_profiles?.name && (
                                  <p className="text-sm font-medium text-gray-800">
                                    {session.orders.user_profiles.name}
                                    {session.orders.user_profiles.phone && (
                                      <span className="text-gray-500 font-normal"> · {session.orders.user_profiles.phone}</span>
                                    )}
                                  </p>
                                )}
                                {session.orders?.delivery_address_snapshot && (
                                  <p className="text-xs text-gray-500">
                                    {[session.orders.delivery_address_snapshot.physical_address,
                                      session.orders.delivery_address_snapshot.area]
                                      .filter(Boolean).join(', ')}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Picker: {session.pickerEmail}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Elapsed Time</p>
                              <p className="text-lg font-bold text-gray-900">{session.elapsedMinutes} min</p>
                            </div>
                          </div>
                          
                          <div className="mb-2">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-semibold text-blue-600">
                                {session.pickedItems} / {session.totalItems} items
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${session.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                      No active picking sessions
                    </div>
                  )}
                </div>

                {/* Recent Completions */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Recent Completions</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pick #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Picker</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentCompletions.map((completion) => (
                          <tr key={completion.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="font-bold text-blue-600">{completion.pick_number}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              #{completion.orders?.order_number}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <p className="text-gray-900 font-medium">{completion.customer?.name || '—'}</p>
                              {completion.customer?.phone && (
                                <p className="text-gray-500 text-xs">{completion.customer.phone}</p>
                              )}
                              {completion.orders?.delivery_address_snapshot && (
                                <p className="text-gray-500 text-xs">
                                  {[completion.orders.delivery_address_snapshot.physical_address,
                                    completion.orders.delivery_address_snapshot.area].filter(Boolean).join(', ')}
                                </p>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {completion.pickerEmail}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {completion.pickTimeDisplay}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {completion.has_shortages ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                                  <AlertCircle size={12} />
                                  Shortages
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                  <CheckCircle size={12} />
                                  Complete
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewDetails(completion)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="View Details"
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={() => handleReprintLabel(completion)}
                                  className="text-gray-600 hover:text-gray-800"
                                  title="Reprint Label"
                                >
                                  <Printer size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Picker Performance Comparison</h3>
                  {performanceStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={performanceStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="pickerName" angle={-45} textAnchor="end" height={100} />
                        <YAxis yAxisId="left" label={{ value: 'Orders', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Time (min)', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="ordersCompleted" fill="#3b82f6" name="Orders Completed" />
                        <Line yAxisId="right" type="monotone" dataKey="avgPickTime" stroke="#10b981" strokeWidth={2} name="Avg Pick Time" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No performance data available</p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Picker Accuracy</h3>
                  {performanceStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={performanceStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="pickerName" angle={-45} textAnchor="end" height={100} />
                        <YAxis label={{ value: 'Accuracy (%)', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="accuracy" fill="#10b981" name="Accuracy %" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No accuracy data available</p>
                  )}
                </div>

                {/* Performance Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Picker</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfect Picks</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {performanceStats.map((stat) => (
                        <tr key={stat.picker_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {stat.pickerName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {stat.ordersCompleted}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {stat.avgPickTime.toFixed(1)} min
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {stat.perfectPicks}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              stat.accuracy >= 95 ? 'bg-green-100 text-green-800' :
                              stat.accuracy >= 85 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {stat.accuracy.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Trends Tab */}
            {activeTab === 'trends' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Hourly Picking Volume & Speed</h3>
                  {hourlyTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={hourlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis yAxisId="left" label={{ value: 'Orders', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Avg Time (min)', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Area yAxisId="left" type="monotone" dataKey="orders" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.6} name="Orders Completed" />
                        <Line yAxisId="right" type="monotone" dataKey="avgPickTime" stroke="#f59e0b" strokeWidth={2} name="Avg Pick Time" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No hourly trend data available</p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Pick Time Distribution</h3>
                  {pickTimeDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={pickTimeDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis label={{ value: 'Number of Orders', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10b981" name="Orders" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No distribution data available</p>
                  )}
                </div>
              </div>
            )}

            {/* Shortages Tab */}
            {activeTab === 'shortages' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Shortage Reasons</h3>
                    {shortageAnalysis.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={shortageAnalysis}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ reason, percent }) => `${reason}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {shortageAnalysis.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-500">No shortage data available</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Shortage Breakdown</h3>
                    <div className="space-y-3">
                      {shortageAnalysis.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="font-medium text-gray-900">{item.reason}</span>
                          </div>
                          <span className="text-lg font-bold text-gray-700">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Shortage Impact Analysis</h3>
                  {hourlyTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={hourlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="shortages" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Orders with Shortages" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No shortage trend data available</p>
                  )}
                </div>
              </div>
            )}

            {/* Wallet Approvals Tab */}
            {activeTab === 'wallet' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="text-blue-600" size={20} />
                    <p className="text-sm text-blue-800">
                      <strong>Approve wallet credits</strong> for items that were unavailable during picking. Credits will be added to customer wallets immediately.
                    </p>
                  </div>
                </div>

                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
                    <p className="text-gray-600">No pending wallet approvals at this time.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map((approval) => (
                      <div key={approval.orderId} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Order #{approval.orderNumber}</h3>
                            <p className="text-sm text-gray-600">{approval.userName} ({approval.userEmail})</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(approval.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Total Refund</p>
                            <p className="text-3xl font-bold text-red-600">R{approval.totalRefund.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">
                            Unavailable Items ({approval.shortages.length})
                          </h4>
                          <div className="space-y-2">
                            {approval.shortages.map((shortage, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                {shortage.productImage && (
                                  <img 
                                    src={shortage.productImage} 
                                    alt={shortage.productName}
                                    className="w-12 h-12 rounded object-cover"
                                  />
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900 text-sm">{shortage.productName}</p>
                                  <p className="text-xs text-gray-600">
                                    Expected: {shortage.expectedQty} | Picked: {shortage.pickedQty} | Short: {shortage.shortQty}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Reason: {shortage.reason?.replace(/_/g, ' ')}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-red-600">-R{shortage.refund.toFixed(2)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ID Cards Tab */}
            {activeTab === 'idcards' && (
              <IDCardsSection 
                pickers={pickers}
                selectedPickers={selectedPickers}
                loadingPickers={loadingPickers}
                setPickers={setPickers}
                setSelectedPickers={setSelectedPickers}
                setLoadingPickers={setLoadingPickers}
                printRef={printRef}
              />
            )}
          </div>
        </div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
              <p className="mt-4 text-gray-700">Loading dashboard...</p>
            </div>
          </div>
        )}
      </div>

      {/* Reprint Modal */}
      {reprintModal && (
        <>
          <style>{`
            @media print {
              body * { visibility: hidden; }
              .print-area, .print-area * { visibility: visible; }
              .print-area { 
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              .no-print { display: none !important; }
            }
          `}</style>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 print-area">
              <div className="flex justify-between items-center mb-6 no-print">
                <h2 className="text-2xl font-bold text-gray-900">Reprint Pick Label</h2>
                <button
                  onClick={() => {
                    setReprintModal(null);
                    setQrCodeUrl('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="text-center">
                <div className="bg-blue-50 rounded-xl p-6 mb-6">
                  <p className="text-sm text-gray-600 mb-2">Pick Number</p>
                  <p className="text-6xl font-bold text-blue-600 mb-2">
                    {reprintModal.pick_number}
                  </p>
                  <p className="text-sm text-gray-600">Order #{reprintModal.orders?.order_number}</p>
                </div>

                {qrCodeUrl && (
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <img
                      src={qrCodeUrl}
                      alt="Order QR Code"
                      className="mx-auto mb-2"
                    />
                    <p className="text-xs text-gray-600">Scan for verification</p>
                  </div>
                )}

                <button
                  onClick={handlePrint}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors no-print"
                >
                  <Printer size={20} />
                  Print Label
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Order Details Modal */}
      {orderDetailsModal && orderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-8 my-8">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                <p className="text-sm text-gray-600">
                  Order #{orderDetails.order_number} • Pick #{orderDetails.pickingTask.pick_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setOrderDetailsModal(false);
                  setOrderDetails(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Picker</p>
                <p className="font-semibold text-gray-900">{orderDetails.pickingTask.pickerEmail}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Pick Time</p>
                <p className="font-semibold text-gray-900">{orderDetails.pickingTask.pickTimeDisplay}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Total Items</p>
                <p className="font-semibold text-gray-900">{orderDetails.order_items?.length || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Status</p>
                {orderDetails.pickingTask.has_shortages ? (
                  <span className="inline-flex items-center gap-1 text-orange-700 font-semibold text-sm">
                    <AlertCircle size={14} />
                    Shortages
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-green-700 font-semibold text-sm">
                    <CheckCircle size={14} />
                    Complete
                  </span>
                )}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Order Items</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {orderDetails.order_items?.map((item, index) => {
                  const shortage = item.shortage_info || orderDetails.shortageDetails?.find(
                    s => s.product_id === item.product_id
                  );
                  
                  const orderedQty = shortage?.ordered || shortage?.ordered_qty || item.quantity;
                  const pickedQty = shortage ? (shortage.picked ?? shortage.picked_qty ?? 0) : (item.picked_quantity ?? orderedQty);
                  const shortageQty = orderedQty - pickedQty;
                  
                  const isFullyPicked = shortageQty === 0;
                  const isPartialShortage = shortageQty > 0 && pickedQty > 0;
                  const isFullShortage = pickedQty === 0;

                  return (
                    <div 
                      key={item.id || `item-${index}`}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 ${
                        isFullShortage ? 'bg-red-50 border-red-200' :
                        isPartialShortage ? 'bg-orange-50 border-orange-200' :
                        'bg-green-50 border-green-200'
                      }`}
                    >
                      <img
                        src={item.products?.image || '/placeholder.png'}
                        alt={item.products?.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{item.products?.name || shortage?.product_name}</h4>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-600">
                            Ordered: <span className="font-semibold">{orderedQty}</span>
                          </span>
                          <span className="text-sm text-gray-600">
                            Picked: <span className={`font-semibold ${shortageQty > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                              {pickedQty}
                            </span>
                          </span>
                          {shortageQty > 0 && (
                            <span className={`text-sm font-semibold ${
                              isFullShortage ? 'text-red-700' : 'text-orange-700'
                            }`}>
                              Short: {shortageQty}
                            </span>
                          )}
                        </div>
                        {shortage?.reason && (
                          <p className="text-xs text-gray-600 mt-1">
                            Reason: {shortage.reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                        )}
                      </div>
                      <div>
                        {isFullyPicked ? (
                          <div className="bg-green-100 p-2 rounded-lg">
                            <CheckCircle size={24} className="text-green-600" />
                          </div>
                        ) : isFullShortage ? (
                          <div className="bg-red-100 p-2 rounded-lg">
                            <XCircle size={24} className="text-red-600" />
                          </div>
                        ) : (
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <AlertCircle size={24} className="text-orange-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {orderDetails.pickingTask.has_shortages && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                <h3 className="font-bold text-orange-900 mb-2 flex items-center gap-2">
                  <AlertCircle size={20} />
                  Shortage Summary
                </h3>
                <p className="text-sm text-orange-800">
                  {orderDetails.shortageDetails?.length || 0} item(s) with shortages
                  {orderDetails.pickingTask.shortage_value && (
                    <> • Value: R{orderDetails.pickingTask.shortage_value.toFixed(2)}</>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminPickingDashboard;
